import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FeedbackBanner } from './MvpTalentUi';

type EditorMode = 'profile' | 'banner';

type FocalPoint = {
  x: number;
  y: number;
};

interface MemberImageEditorProps {
  open: boolean;
  mode: EditorMode;
  memberName: string;
  currentImageUrl?: string | null;
  onClose: () => void;
  onSave: (payload: { file: File; focalPoint?: FocalPoint | null }) => Promise<void>;
}

interface EditorConfig {
  title: string;
  buttonLabel: string;
  helperText: string;
  aspectRatio: number;
  maxFileSizeBytes: number;
  acceptedMimeTypes: string[];
  outputWidth: number;
  outputHeight: number;
  minimumWidth?: number;
  minimumHeight?: number;
}

interface LoadedImageState {
  width: number;
  height: number;
  image: HTMLImageElement;
}

const ACCEPTED_FORMAT_TEXT = 'JPG, JPEG, PNG, or WebP';

const EDITOR_CONFIG: Record<EditorMode, EditorConfig> = {
  profile: {
    title: 'Update profile image',
    buttonLabel: 'Save profile image',
    helperText: 'Upload a square image and position it before saving. Final output is 500 x 500.',
    aspectRatio: 1,
    maxFileSizeBytes: 5 * 1024 * 1024,
    acceptedMimeTypes: ['image/jpeg', 'image/png', 'image/webp'],
    outputWidth: 500,
    outputHeight: 500,
  },
  banner: {
    title: 'Update banner image',
    buttonLabel: 'Save banner image',
    helperText: 'Upload a wide image and reposition it before saving. Final output is 1600 x 500.',
    aspectRatio: 16 / 5,
    maxFileSizeBytes: 8 * 1024 * 1024,
    acceptedMimeTypes: ['image/jpeg', 'image/png', 'image/webp'],
    outputWidth: 1600,
    outputHeight: 500,
    minimumWidth: 1200,
    minimumHeight: 375,
  },
};

const PREVIEW_FRAME_WIDTH: Record<EditorMode, number> = {
  profile: 320,
  banner: 640,
};

const clamp = (value: number, min: number, max: number): number => {
  if (Number.isNaN(value)) return min;
  return Math.min(Math.max(value, min), max);
};

const formatMegabytes = (bytes: number): string => `${Math.round((bytes / (1024 * 1024)) * 10) / 10} MB`;

const readImageDimensions = (file: File): Promise<LoadedImageState> => new Promise((resolve, reject) => {
  const objectUrl = URL.createObjectURL(file);
  const image = new Image();

  image.onload = () => {
    resolve({
      width: image.naturalWidth || image.width,
      height: image.naturalHeight || image.height,
      image,
    });
    URL.revokeObjectURL(objectUrl);
  };

  image.onerror = () => {
    URL.revokeObjectURL(objectUrl);
    reject(new Error('Unable to read this image. Please choose a different file.'));
  };

  image.src = objectUrl;
});

const canvasToBlob = (canvas: HTMLCanvasElement, type: string): Promise<Blob> => new Promise((resolve, reject) => {
  canvas.toBlob((blob) => {
    if (!blob) {
      reject(new Error('Unable to prepare this image for upload.'));
      return;
    }

    resolve(blob);
  }, type, 0.9);
});

const computePositionBounds = (
  frameWidth: number,
  frameHeight: number,
  renderedWidth: number,
  renderedHeight: number
) => ({
  minX: Math.min(0, (frameWidth - renderedWidth) / 2),
  maxX: Math.max(0, (renderedWidth - frameWidth) / 2),
  minY: Math.min(0, (frameHeight - renderedHeight) / 2),
  maxY: Math.max(0, (renderedHeight - frameHeight) / 2),
});

const computeRenderState = ({
  frameWidth,
  frameHeight,
  imageWidth,
  imageHeight,
  zoom,
  positionX,
  positionY,
}: {
  frameWidth: number;
  frameHeight: number;
  imageWidth: number;
  imageHeight: number;
  zoom: number;
  positionX: number;
  positionY: number;
}) => {
  const baseScale = Math.max(frameWidth / imageWidth, frameHeight / imageHeight);
  const scaledWidth = imageWidth * baseScale * zoom;
  const scaledHeight = imageHeight * baseScale * zoom;
  const bounds = computePositionBounds(frameWidth, frameHeight, scaledWidth, scaledHeight);
  const clampedX = clamp(positionX, bounds.minX, bounds.maxX);
  const clampedY = clamp(positionY, bounds.minY, bounds.maxY);
  const left = (frameWidth - scaledWidth) / 2 + clampedX;
  const top = (frameHeight - scaledHeight) / 2 + clampedY;

  return {
    width: scaledWidth,
    height: scaledHeight,
    left,
    top,
    clampedX,
    clampedY,
  };
};

const MemberImageEditor: React.FC<MemberImageEditorProps> = ({
  open,
  mode,
  memberName,
  currentImageUrl,
  onClose,
  onSave,
}) => {
  const config = EDITOR_CONFIG[mode];
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const previewImageRef = useRef<HTMLImageElement | null>(null);
  const dragStateRef = useRef<{ startX: number; startY: number; startPositionX: number; startPositionY: number } | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [loadedImage, setLoadedImage] = useState<LoadedImageState | null>(null);
  const [zoom, setZoom] = useState(1);
  const [positionX, setPositionX] = useState(0);
  const [positionY, setPositionY] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const frameWidth = PREVIEW_FRAME_WIDTH[mode];
  const frameHeight = Math.round(frameWidth / config.aspectRatio);

  useEffect(() => {
    if (!open) {
      setSelectedFile(null);
      setLoadedImage(null);
      setZoom(1);
      setPositionX(0);
      setPositionY(0);
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    if (!selectedFile) {
      setPreviewUrl(null);
      return undefined;
    }

    const objectUrl = URL.createObjectURL(selectedFile);
    setPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedFile]);

  const renderState = useMemo(() => {
    if (!loadedImage) return null;
    return computeRenderState({
      frameWidth,
      frameHeight,
      imageWidth: loadedImage.width,
      imageHeight: loadedImage.height,
      zoom,
      positionX,
      positionY,
    });
  }, [frameHeight, frameWidth, loadedImage, positionX, positionY, zoom]);

  useEffect(() => {
    if (!renderState) return;
    if (renderState.clampedX !== positionX) setPositionX(renderState.clampedX);
    if (renderState.clampedY !== positionY) setPositionY(renderState.clampedY);
  }, [positionX, positionY, renderState]);

  const canSave = Boolean(selectedFile && loadedImage && renderState);

  if (!open) return null;

  const validateFile = async (file: File): Promise<void> => {
    if (!config.acceptedMimeTypes.includes(file.type)) {
      throw new Error(`Unsupported file type. Please upload ${ACCEPTED_FORMAT_TEXT}.`);
    }

    if (file.size > config.maxFileSizeBytes) {
      throw new Error(`This file is too large. Please upload an image under ${formatMegabytes(config.maxFileSizeBytes)}.`);
    }

    const dimensions = await readImageDimensions(file);

    if (config.minimumWidth && dimensions.width < config.minimumWidth) {
      throw new Error(`This image is too small. Minimum size is ${config.minimumWidth} x ${config.minimumHeight}.`);
    }

    if (config.minimumHeight && dimensions.height < config.minimumHeight) {
      throw new Error(`This image is too small. Minimum size is ${config.minimumWidth} x ${config.minimumHeight}.`);
    }

    setLoadedImage(dimensions);
  };

  const handleFileSelection = async (fileList: FileList | null) => {
    const file = fileList?.[0];
    if (!file) return;

    setError(null);

    try {
      await validateFile(file);
      setSelectedFile(file);
      setZoom(1);
      setPositionX(0);
      setPositionY(0);
    } catch (validationError) {
      setSelectedFile(null);
      setLoadedImage(null);
      setError(validationError instanceof Error ? validationError.message : 'Unable to use this file.');
    }
  };

  const handlePointerStart = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!renderState) return;

    event.preventDefault();
    dragStateRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      startPositionX: positionX,
      startPositionY: positionY,
    };
  };

  const handlePointerMove = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!renderState || !dragStateRef.current) return;

    const nextX = dragStateRef.current.startPositionX + (event.clientX - dragStateRef.current.startX);
    const nextY = dragStateRef.current.startPositionY + (event.clientY - dragStateRef.current.startY);
    const bounds = computePositionBounds(frameWidth, frameHeight, renderState.width, renderState.height);

    setPositionX(clamp(nextX, bounds.minX, bounds.maxX));
    setPositionY(clamp(nextY, bounds.minY, bounds.maxY));
  };

  const handlePointerEnd = () => {
    dragStateRef.current = null;
  };

  const handleSave = async () => {
    if (!selectedFile || !renderState || !loadedImage?.image) return;

    setSaving(true);
    setError(null);

    try {
      const canvas = document.createElement('canvas');
      canvas.width = config.outputWidth;
      canvas.height = config.outputHeight;
      const context = canvas.getContext('2d');

      if (!context) {
        throw new Error('Your browser could not prepare this image for upload.');
      }

      const scaleX = config.outputWidth / frameWidth;
      const scaleY = config.outputHeight / frameHeight;

      context.drawImage(
        loadedImage.image,
        renderState.left * scaleX,
        renderState.top * scaleY,
        renderState.width * scaleX,
        renderState.height * scaleY
      );

      const processedBlob = await canvasToBlob(canvas, 'image/webp');
      const uploadFile = new File(
        [processedBlob],
        `${mode}-${selectedFile.name.replace(/\.[^.]+$/, '') || 'image'}.webp`,
        { type: 'image/webp' }
      );

      let focalPoint: FocalPoint | null = null;
      if (mode === 'banner') {
        focalPoint = {
          x: Number((((frameWidth / 2) - renderState.left) / renderState.width).toFixed(4)),
          y: Number((((frameHeight / 2) - renderState.top) / renderState.height).toFixed(4)),
        };
      }

      await onSave({ file: uploadFile, focalPoint });
      onClose();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Unable to save this image.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mvp-dialog-backdrop" role="presentation" onClick={() => !saving && onClose()}>
      <div
        className="mvp-dialog mvp-dialog--wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`member-image-editor-${mode}`}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mvp-member-image-editor__header">
          <div>
            <h3 id={`member-image-editor-${mode}`}>{config.title}</h3>
            <p>{config.helperText}</p>
          </div>
          <button type="button" className="mvp-button mvp-button--ghost" onClick={onClose} disabled={saving}>
            Close
          </button>
        </div>

        {error ? <FeedbackBanner tone="error" message={error} /> : null}

        <div className="mvp-member-image-editor__controls">
          <button
            type="button"
            className="mvp-button"
            onClick={() => fileInputRef.current?.click()}
            disabled={saving}
          >
            Choose image
          </button>
          <span>
            Accepted: {ACCEPTED_FORMAT_TEXT}. Max file size: {formatMegabytes(config.maxFileSizeBytes)}.
          </span>
          <input
            ref={fileInputRef}
            type="file"
            accept={config.acceptedMimeTypes.join(',')}
            hidden
            onChange={(event) => {
              void handleFileSelection(event.target.files);
              event.target.value = '';
            }}
          />
        </div>

        <div className={`mvp-member-image-editor__workspace mvp-member-image-editor__workspace--${mode}`}>
          <div className="mvp-member-image-editor__preview-shell">
            <div
              className={`mvp-member-image-editor__preview mvp-member-image-editor__preview--${mode}`}
              style={{ width: frameWidth, height: frameHeight }}
              onMouseDown={handlePointerStart}
              onMouseMove={handlePointerMove}
              onMouseUp={handlePointerEnd}
              onMouseLeave={handlePointerEnd}
            >
              {previewUrl && renderState ? (
                <img
                  ref={previewImageRef}
                  src={previewUrl}
                  alt={`${memberName} ${mode} preview`}
                  draggable={false}
                  style={{
                    width: renderState.width,
                    height: renderState.height,
                    left: renderState.left,
                    top: renderState.top,
                  }}
                />
              ) : currentImageUrl ? (
                <img
                  className="mvp-member-image-editor__existing"
                  src={currentImageUrl}
                  alt={`${memberName} current ${mode}`}
                />
              ) : (
                <div className="mvp-member-image-editor__empty">
                  <strong>No {mode} image selected yet</strong>
                  <span>Choose an image to preview the crop.</span>
                </div>
              )}
              <div className="mvp-member-image-editor__crop-frame" aria-hidden="true" />
            </div>
          </div>

          <div className="mvp-member-image-editor__sidebar">
            <label className="mvp-field" htmlFor={`member-image-zoom-${mode}`}>
              <span>Zoom</span>
              <input
                id={`member-image-zoom-${mode}`}
                type="range"
                min="1"
                max="3"
                step="0.01"
                value={zoom}
                onChange={(event) => setZoom(Number(event.target.value))}
                disabled={!selectedFile || saving}
              />
            </label>
            <p className="mvp-member-image-editor__hint">
              Drag the image inside the crop frame to reposition it before saving.
            </p>
          </div>
        </div>

        <div className="mvp-dialog__actions">
          <button type="button" className="mvp-button mvp-button--ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className="mvp-button"
            onClick={handleSave}
            disabled={!canSave || saving}
          >
            {saving ? 'Saving…' : config.buttonLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MemberImageEditor;
